/**
 * Which of the six things the table is showing, decided ONCE.
 *
 * `pending`, `error` and `empty` are three states and never a branch on the data: `rows.length === 0`
 * cannot tell a read still running from one that answered with nothing, nor either from a filter
 * that matched none, and every jump and every wrong sentence of the September round came from that
 * conflation. It is `pages/plan-library`'s `planListStatus` on this screen's own five absences.
 *
 * ── The ORDER of the questions is the rule ──────────────────────────────────────────────────
 * The read first, then the SCOPE's own emptiness, then the filter's. So a trainer who has created
 * no exercises and has also typed something is told they have none of their own rather than sent to
 * clear a filter that is not in their way.
 *
 * ── Two empties, because they have different ways out ───────────────────────────────────────
 * `emptyCatalogue` is the whole library being empty, which on this screen means something is wrong
 * upstream — there is no way for a trainer to have no exercises at all, the catalogue is 477 rows
 * of shared content. `emptyScope` is `Twoje` with nothing in it, which is the ordinary first visit
 * and whose way out is the header's `+`. Collapsing them would put "add your first exercise" under
 * a library that failed to seed.
 *
 * ── The panel beside the table draws NOTHING in four of the six ─────────────────────────────
 * The canon does not put a second column beside a failure or an empty table: the card 400px away
 * has just said what happened, and a column of empty cards would say it again in a worse way. So
 * there is no "absence" projection of this type for the panel to read — there was one, and it had
 * no call site because there is nothing for it to answer. The column is drawn for `pending` and for
 * `rows`, which is two readings of the SCREEN's own status and not of this one.
 */

import type { ExerciseScope } from './exerciseScope';

export type ExerciseListStatus =
  | 'pending'
  | 'error'
  | 'emptyCatalogue'
  | 'emptyScope'
  | 'filtered'
  /** „Do uzupełnienia" pressed with nothing else narrowing, and nothing in the scope marked: good news. */
  | 'reviewDone'
  | 'rows';

export interface ExerciseListReading {
  readonly read: 'loading' | 'error' | 'ready';
  readonly scope: ExerciseScope;
  /** The whole catalogue, unfiltered and unscoped. */
  readonly catalogueCount: number;
  /** What the SCOPE holds, before the search box and the facets narrow it. */
  readonly scopeCount: number;
  /** What the table is actually drawing. */
  readonly rowCount: number;
  /** Board D's „Do uzupełnienia" toggle is pressed: only marked movements are shown. */
  readonly reviewOnly: boolean;
  /** A search term or a facet narrows the list too. */
  readonly narrowed: boolean;
}

export function exerciseListStatus(reading: ExerciseListReading): ExerciseListStatus {
  if (reading.read === 'loading') return 'pending';
  if (reading.read === 'error') return 'error';
  if (reading.catalogueCount === 0) return 'emptyCatalogue';
  if (reading.scopeCount === 0) return 'emptyScope';
  if (reading.rowCount > 0) return 'rows';
  // The toggle alone emptied the list: every movement here is checked. Anything else narrowing it
  // is a filter to clear, the toggle with it.
  return reading.reviewOnly && !reading.narrowed ? 'reviewDone' : 'filtered';
}
